import * as React from 'react';
import { cn } from '../../lib/utils';

export type TypographyVariant =
  | 'text-xs'
  | 'text-sm'
  | 'text-md'
  | 'text-lg'
  | 'text-xl'
  | 'text-2xl'
  | 'header-xs'
  | 'header-sm'
  | 'header-md'
  | 'header-lg'
  | 'header-xl'
  | 'header-2xl'
  | 'header-3xl'
  | 'header-4xl';

export type TypographyWeight = 'light' | 'regular' | 'medium' | 'semibold' | 'bold';

const DEFAULT_TAG: Record<TypographyVariant, React.ElementType> = {
  'header-4xl': 'h1',
  'header-3xl': 'h2',
  'header-2xl': 'h3',
  'header-xl': 'h4',
  'header-lg': 'h5',
  'header-md': 'h6',
  'header-sm': 'p',
  'header-xs': 'p',
  'text-2xl': 'p',
  'text-xl': 'p',
  'text-lg': 'p',
  'text-md': 'p',
  'text-sm': 'p',
  'text-xs': 'p',
};

const WEIGHT_CLASS: Record<TypographyWeight, string> = {
  light: 'font-light',
  regular: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

export interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  variant: TypographyVariant;
  weight?: TypographyWeight;
  as?: React.ElementType;
}

/** Renders text using a design-token type role (size, line-height, family) with an element that matches the variant by default. */
export function Typography({ variant, weight, as, className, children, ...rest }: TypographyProps) {
  const Tag = as ?? DEFAULT_TAG[variant];
  const isHeader = variant.startsWith('header-');
  const resolvedWeight = weight ?? (isHeader ? 'semibold' : 'regular');

  return (
    <Tag className={cn(`type-${variant}`, WEIGHT_CLASS[resolvedWeight], className)} {...rest}>
      {children}
    </Tag>
  );
}
